import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import { Calendar, CalendarDays, CalendarRange, Settings2, SlidersHorizontal, X } from 'lucide-react';
import { useMonthlyTrendQuery } from '../hooks/useQueries';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useTheme } from '../context/ThemeContext';
import useModalBack from '../hooks/useModalBack';
import DateTimeInput from '../components/common/DateTimeInput';
import MonthlyTrendChart from '../components/dashboard/MonthlyTrendChart';

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const PERIOD_OPTIONS = [
  { value: 'thisMonth', label: 'This Month', icon: CalendarDays, iconColor: '#8b5cf6' },
  { value: 'lastMonth', label: 'Last Month', icon: Calendar, iconColor: '#3b82f6' },
  { value: 'annually', label: 'This Year', icon: CalendarRange, iconColor: '#22c55e' },
  { value: 'custom', label: 'Custom', icon: Settings2, iconColor: '#6b7280' }
];

export default function TrendPage() {
  const { hasEncryptionKey } = useAuth();
  const { formatAmount, config } = useConfig();
  const { isModernUI } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const period = searchParams.get('period') || 'annually';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  // Filter modal state with hardware back button support
  const [showFilterModal, setShowFilterModal] = useState(false);
  const closeFilterModal = useCallback(() => setShowFilterModal(false), []);
  const openFilterModal = useCallback(() => setShowFilterModal(true), []);
  const { closeSilent: closeSilentFilter } = useModalBack(showFilterModal, closeFilterModal, 'TrendFilter');

  // Temp filter state for modal
  const [tempPeriod, setTempPeriod] = useState(period);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [autoOpenEndDate, setAutoOpenEndDate] = useState(false);

  // Desktop date local state — source of truth (URL params have async delay)
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);

  useEffect(() => {
    if (showFilterModal) {
      setTempPeriod(period);
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [showFilterModal, period, startDate, endDate]);

  // Build query params
  const queryParams = useMemo(() => {
    const params = { period };
    if (period === 'custom') {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    }
    return params;
  }, [period, startDate, endDate]);

  const { data: trendData, isLoading } = useMonthlyTrendQuery(queryParams, {
    enabled: hasEncryptionKey
  });

  // Update URL params
  const updateFilter = (key, value, extraUpdates = {}) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    Object.entries(extraUpdates).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
      else newParams.delete(k);
    });
    setSearchParams(newParams);
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('period', tempPeriod);
    if (tempPeriod === 'custom' && tempStartDate) newParams.set('startDate', tempStartDate);
    else newParams.delete('startDate');
    if (tempPeriod === 'custom' && tempEndDate) newParams.set('endDate', tempEndDate);
    else newParams.delete('endDate');
    // Close modal silently (no history.back), replace the modal's history entry with new params
    closeSilentFilter();
    setSearchParams(newParams, { replace: true });
  };

  // Compute totals from trend data
  const totals = useMemo(() => {
    const trend = trendData?.monthlyTrend;
    if (!trend) return { income: 0, expense: 0 };
    return Object.values(trend).reduce(
      (acc, month) => ({
        income: acc.income + (month.income || 0),
        expense: acc.expense + (month.expense || 0),
      }),
      { income: 0, expense: 0 }
    );
  }, [trendData]);

  const incomeColor = config.incomeColor || '#10b981';
  const expenseColor = config.expenseColor || '#ef4444';

  // View toggle: separate or combined charts
  const [viewMode, setViewMode] = useState('separate');

  const net = totals.income - totals.expense;
  const savingsRate = totals.income > 0 ? Math.round((net / totals.income) * 100) : 0;

  // Has active filters?
  const hasActiveFilters = period !== 'annually';

  return (
    <div className="space-y-3 pb-4">
      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden flex items-center justify-between mb-1">
        <div>
          <h1 className="text-base font-bold text-surface-900">Trend</h1>
          <p className="text-[10px] text-surface-500">Income vs Expense over time</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/transactions')}
            className="flex items-center justify-center w-9 h-9 rounded-xl border bg-white border-surface-200 text-surface-600 hover:border-surface-300 transition-all"
            title="Transactions"
          >
            <span className="text-base">📖</span>
          </button>
          <button
            onClick={openFilterModal}
            className={`relative w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${
              hasActiveFilters
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-surface-200 text-surface-600'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
                1
              </span>
            )}
          </button>
        </div>
      </StickyPageHeader>

      {/* Mobile Filter Bottom Sheet */}
      {showFilterModal && createPortal(
        <div className="xl:hidden fixed inset-0 z-50 flex items-end !mt-0" onClick={closeFilterModal}>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Bottom Sheet */}
          <div
            className="relative w-full bg-white rounded-t-2xl animate-slide-up max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h2 className="text-[15px] font-bold text-surface-900">Filter Trend</h2>
              <div className="flex items-center gap-2">
                {(tempPeriod !== 'annually' || tempStartDate || tempEndDate) && (
                  <button
                    onClick={() => {
                      setTempPeriod('annually');
                      setTempStartDate('');
                      setTempEndDate('');
                    }}
                    className="text-xs font-semibold px-2 py-1"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={closeFilterModal}
                  className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-4 pb-6 space-y-4">
              {/* Time Period */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Time Period</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTempPeriod(option.value)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Date Range */}
              {tempPeriod === 'custom' && (
                <div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempStartDate}
                        onChange={(date) => setTempStartDate(date)}
                        placeholder="From"
                        maxDate={tempEndDate || formatLocalDate(new Date())}
                      />
                    </div>
                    <span className="text-surface-300 text-sm">&rarr;</span>
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempEndDate}
                        onChange={(date) => setTempEndDate(date)}
                        placeholder="To"
                        minDate={tempStartDate || undefined}
                        maxDate={formatLocalDate(new Date())}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Apply Button */}
              <button
                onClick={applyFilters}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all shadow-lg"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Desktop Header */}
      <div className="hidden xl:block">
        <h1 className="text-xl font-bold text-surface-900">Income vs Expense Trend</h1>
        <p className="text-sm text-surface-500">Monthly breakdown over time</p>
      </div>

      {/* Desktop Card — Tab + Filters (same layout as Stats) */}
      <div className="hidden xl:block card p-4 space-y-4">
        {/* Separate / Combined Toggle */}
        <div className="grid grid-cols-2 gap-1 p-1 bg-surface-100 rounded-xl">
          <button
            onClick={() => setViewMode('separate')}
            className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
              viewMode === 'separate' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500'
            }`}
          >
            Separate
          </button>
          <button
            onClick={() => setViewMode('combined')}
            className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
              viewMode === 'combined' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500'
            }`}
          >
            Combined
          </button>
        </div>
        {/* Period + Custom Date Filters */}
        <div className="grid grid-cols-2 gap-3">
          <select
            value={period}
            onChange={(e) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('period', e.target.value);
              if (e.target.value !== 'custom') {
                newParams.delete('startDate');
                newParams.delete('endDate');
              }
              setSearchParams(newParams);
            }}
            className="select text-sm bg-surface-50 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 w-auto"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {period === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <DateTimeInput
              dateOnly
              value={localStartDate}
              onChange={(date) => {
                setLocalStartDate(date);
                if (localEndDate && date > localEndDate) {
                  setLocalEndDate('');
                  updateFilter('startDate', date, { endDate: '' });
                } else {
                  updateFilter('startDate', date);
                }
              }}
              placeholder="Start date"
            />
            <DateTimeInput
              dateOnly
              value={localEndDate}
              onChange={(date) => {
                setLocalEndDate(date);
                updateFilter('endDate', date);
              }}
              placeholder="End date"
              minDate={localStartDate || undefined}
          />
          </div>
        )}
      </div>

      {/* Mobile Separate / Combined Toggle */}
      <div className="xl:hidden grid grid-cols-2 gap-1 p-1 bg-surface-100 rounded-xl">
        <button
          onClick={() => setViewMode('separate')}
          className={`py-2 rounded-lg font-medium text-xs transition-all ${
            viewMode === 'separate'
              ? 'bg-white text-surface-900 shadow-sm'
              : 'text-surface-500'
          }`}
        >
          Separate
        </button>
        <button
          onClick={() => setViewMode('combined')}
          className={`py-2 rounded-lg font-medium text-xs transition-all ${
            viewMode === 'combined'
              ? 'bg-white text-surface-900 shadow-sm'
              : 'text-surface-500'
          }`}
        >
          Combined
        </button>
      </div>

      {/* Summary Strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl border border-surface-100 p-3 text-center">
          <p className="text-[10px] text-surface-400 mb-0.5">Income</p>
          <p className="font-bold text-sm" style={{ color: incomeColor }}>
            {formatAmount(totals.income)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-surface-100 p-3 text-center">
          <p className="text-[10px] text-surface-400 mb-0.5">Expense</p>
          <p className="font-bold text-sm" style={{ color: expenseColor }}>
            {formatAmount(totals.expense)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-surface-100 p-3 text-center">
          <p className="text-[10px] text-surface-400 mb-0.5">Net</p>
          <p className={`font-bold text-sm ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatAmount(net)}
          </p>
        </div>
      </div>

      {/* Separate View */}
      {viewMode === 'separate' && (
        <>
          {/* Income Chart */}
          <div className="bg-white rounded-2xl border border-surface-100 p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">📈</span>
                <h3 className="text-sm font-semibold text-surface-900">Income</h3>
              </div>
              <p className="text-sm font-bold" style={{ color: incomeColor }}>{formatAmount(totals.income)}</p>
            </div>
            {isLoading ? (
              <div className="h-[250px] flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <MonthlyTrendChart data={trendData?.monthlyTrend} type="income" granularity={trendData?.granularity} />
            )}
          </div>

          {/* Expense Chart */}
          <div className="bg-white rounded-2xl border border-surface-100 p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">📉</span>
                <h3 className="text-sm font-semibold text-surface-900">Expense</h3>
              </div>
              <p className="text-sm font-bold" style={{ color: expenseColor }}>{formatAmount(totals.expense)}</p>
            </div>
            {isLoading ? (
              <div className="h-[250px] flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <MonthlyTrendChart data={trendData?.monthlyTrend} type="expense" granularity={trendData?.granularity} />
            )}
          </div>
        </>
      )}

      {/* Combined View */}
      {viewMode === 'combined' && (
        <div className="bg-white rounded-2xl border border-surface-100 p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-surface-900">Income vs Expense</h3>
          </div>
          {isLoading ? (
            <div className="h-[250px] flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <MonthlyTrendChart data={trendData?.monthlyTrend} combined granularity={trendData?.granularity} />
          )}
        </div>
      )}

      {/* Net Balance Card with Savings Rate */}
      <div className="bg-white rounded-2xl border border-surface-100 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-surface-500">Net Balance</span>
          <span className={`text-lg font-bold ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatAmount(Math.abs(net))}
          </span>
        </div>
        {totals.income > 0 && (
          <>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-surface-100">
              <span className="text-xs text-surface-400">Savings Rate</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(Math.max(savingsRate, 0), 100)}%`,
                      backgroundColor: savingsRate >= 0 ? incomeColor : expenseColor
                    }}
                  />
                </div>
                <span className={`text-xs font-bold ${savingsRate >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {savingsRate}%
                </span>
              </div>
            </div>
            <p className="text-[10px] text-surface-400 mt-2">
              Savings Rate = (Income - Expense) / Income. Shows how much of your income you saved in this period.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
